import { inject, Injectable } from '@angular/core';
import { FirebaseApp } from '@angular/fire/app';
import { getMessaging } from '@angular/fire/messaging';
import { getToken, Messaging, onMessage, isSupported } from 'firebase/messaging';
import { environment } from '../../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class FirebaseMessagingService {
    private messaging?: Messaging;
    private swRegistration?: ServiceWorkerRegistration;

    constructor() {
        // Separar inicialización async para poder chequear compatibilidad
        void this.init();
    }

    private async init() {
        // Flag para desactivar FCM en builds sin SW (p.ej. testing)
        if (!environment.sw) {
            console.info('FCM deshabilitado: environment.sw = false');
            return;
        }

        // Verificar soporte de Firebase Messaging (navegador / contexto)
        const supported = await isSupported().catch(() => false);
        if (!supported) {
            console.info('FCM no soportado en este navegador/contexto (isSupported=false).');
            return;
        }

        // FCM sólo funciona en contextos seguros (https o localhost) y con SW/Push
        const isSecure = window.isSecureContext || window.location.hostname === 'localhost';
        if (!isSecure) {
            console.info('FCM deshabilitado: contexto no seguro (requiere HTTPS o localhost)');
            return;
        }
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
            console.info('FCM deshabilitado: ServiceWorker/Push no soportados.');
            return;
        }

        const app = inject(FirebaseApp);
        this.messaging = getMessaging(app);

        try {
            this.swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
            console.info('Service Worker registrado para FCM:', this.swRegistration);
        } catch (err) {
            console.error('Error registrando Service Worker para FCM', err);
        }
    }

    async requestPermissionAndGetToken(): Promise<string | null> {
        try {
            // Si no se inicializó (por falta de soporte), salir temprano
            const supported = await isSupported().catch(() => false);
            if (!supported) {
                console.info('FCM: navegador no soportado (isSupported=false).');
                return null;
            }

            const isSecure = window.isSecureContext || window.location.hostname === 'localhost';
            if (!isSecure) {
                console.warn('FCM: contexto no seguro (requiere HTTPS).');
                return null;
            }
            if (!('serviceWorker' in navigator)) {
                console.warn('FCM: ServiceWorker no soportado en este navegador.');
                return null;
            }
            if (!('PushManager' in window)) {
                console.warn('FCM: PushManager no soportado en este navegador.');
                return null;
            }

            if (!this.messaging) {
                console.warn('FCM: messaging no inicializado (posible falta de soporte).');
                return null;
            }

            // Asegurarnos de que el usuario haya concedido permiso de notificaciones
            if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
                const permission = await Notification.requestPermission();
                if (permission !== 'granted') {
                    console.warn('Permiso de notificaciones denegado por el usuario.');
                    return null;
                }
            } else if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
                console.warn('Permiso de notificaciones previamente denegado.');
                return null;
            }

            // Si no tenemos la registration guardada, esperar a que el SW esté listo
            if (!this.swRegistration) {
                try {
                    const ready = await navigator.serviceWorker.ready;
                    this.swRegistration = ready;
                } catch (e) {
                    console.warn('navigator.serviceWorker.ready falló:', e);
                }
            }

            // Diagnóstico: imprimir el estado actual antes de pedir token
            console.debug('FCM diagnóstico: Notification.permission =', typeof Notification !== 'undefined' ? Notification.permission : 'undefined');
            console.debug('FCM diagnóstico: swRegistration =', this.swRegistration);
            if ('serviceWorker' in navigator) {
                try {
                    const regs = await navigator.serviceWorker.getRegistrations();
                    console.debug('FCM diagnóstico: navigator.serviceWorker.getRegistrations() =', regs);
                } catch (e) {
                    console.debug('FCM diagnóstico: getRegistrations fallo', e);
                }
                try {
                    const ready = await navigator.serviceWorker.ready;
                    console.debug('FCM diagnóstico: navigator.serviceWorker.ready =', ready);
                } catch (e) {
                    console.debug('FCM diagnóstico: navigator.serviceWorker.ready fallo', e);
                }
            }

            // Intentar obtener token con reintentos si la registration falla
            const vapidKey = 'BCdyxwB4R0FWKqN_LXPrAN54DyEXbAIRMTeb3rgdy_bzAZDlSUgaRk4tLcZwnuv5mQ8moMgNDC7oO_cJ8tzODxw';
            let lastError: any = null;
            for (let attempt = 1; attempt <= 3; attempt++) {
                try {
                    console.debug(`FCM intento getToken #${attempt}`, { sw: this.swRegistration });
                    const currentToken = await getToken(this.messaging, {
                        vapidKey,
                        serviceWorkerRegistration: this.swRegistration
                    });
                    // Si llegamos acá, OK
                    if (currentToken) {
                        console.info('Token FCM generado:', currentToken);
                        return currentToken;
                    }
                    // no token recibido, continuar reintentos
                    lastError = new Error('No token recibido');
                } catch (e: any) {
                    lastError = e;
                    console.warn(`FCM getToken fallo en intento #${attempt}:`, e && e.message ? e.message : e);
                    // Si el fallo indica problema de registration, esperar y actualizar registration
                    const msg = (e && e.message) ? String(e.message) : 'unknown';
                    if (msg.includes('Registration failed') || msg.includes('push service')) {
                        // esperar un poco y refrescar la registration
                        try {
                            console.debug('FCM reintentando: esperando navigator.serviceWorker.ready');
                            const ready = await navigator.serviceWorker.ready;
                            this.swRegistration = ready;
                            console.debug('FCM nueva swRegistration desde ready =', this.swRegistration);
                        } catch (inner) {
                            console.debug('FCM ready fallo en reintento', inner);
                        }
                        // pequeña espera antes del siguiente intento
                        await new Promise((r) => setTimeout(r, 1000 * attempt));
                        continue;
                    } else {
                        // error diferente, no lo reintentamos más
                        break;
                    }
                }
            }
            // Si llegamos aquí, reportar último error
            if (lastError) {
                console.debug('FCM: todos los reintentos fallaron, ejecutando diagnóstico adicional de PushManager...');
                try {
                    await this.testPushSubscription(vapidKey);
                } catch (diagErr) {
                    console.debug('FCM diagnóstico adicional fallo:', diagErr);
                }
                throw lastError;
            }

            // unreachable: token handling ocurre en el bucle
            return null;
        } catch (err: any) {
            // Mejor detalle para errores de Push/Registration
            if (err && err.code) {
                console.error('Error obteniendo token FCM', err.code, err.message || err);
            } else {
                console.error('Error obteniendo token FCM', err);
            }
            return null;
        }
    }

    listenForMessages() {
        if (!this.messaging) {
            console.info('FCM: no se inició messaging, skip listener.');
            return;
        }

        onMessage(this.messaging, (payload) => {
            console.info('Mensaje recibido en primer plano:', payload);
            // Puedes mostrar una notificación aquí o procesar el payload
        });
    }

    /**
     * Intenta subscribirse directamente al PushManager con la VAPID key
     * para obtener datos de diagnóstico cuando getToken falla.
     */
    private async testPushSubscription(vapidKey: string) {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
            console.debug('FCM testPushSubscription: ServiceWorker o PushManager no soportado en este navegador.');
            return;
        }

        try {
            const registration = await navigator.serviceWorker.ready;
            console.debug('FCM testPushSubscription: registration obtenido =', registration);

            // Convertir VAPID base64 a Uint8Array
            const urlBase64ToUint8Array = (base64String: string) => {
                const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
                const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
                const rawData = window.atob(base64);
                const outputArray = new Uint8Array(rawData.length);
                for (let i = 0; i < rawData.length; ++i) {
                    outputArray[i] = rawData.charCodeAt(i);
                }
                return outputArray;
            };

            const applicationServerKey = urlBase64ToUint8Array(vapidKey);
            console.debug('FCM testPushSubscription: intentando PushManager.subscribe con applicationServerKey');

            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey
            });

            console.info('FCM testPushSubscription: suscripción exitosa', subscription);
            // En caso de éxito, desuscribimos inmediatamente (solo diagnóstico)
            try {
                await subscription.unsubscribe();
                console.debug('FCM testPushSubscription: desuscrito tras diagnóstico');
            } catch (e) {
                console.debug('FCM testPushSubscription: error al desuscribir', e);
            }
        } catch (e: any) {
            console.warn('FCM testPushSubscription: fallo al intentar suscribirse:', e && e.message ? e.message : e);
            throw e;
        }
    }
}
